import assertNever from "assert-never";
import { ChronoUnit, LocalDateTime, LocalTime } from "@js-joda/core";
import React from "react";
import { Page } from "../../activities/AppPage";
import { API } from "../../API";
import useMixin from "../../hooks/useMixin";
import { isLoading, loading, Loading } from "../../Loading";
import { RequiredDutySheetTask, VisitInstanceDetails, VisitInstanceId } from "../../messages";
import { STRINGS } from "../../strings";
import { AppState } from "../../system/AppState";
import { translate } from "../../translate";
import { fmap } from "../../utils/fmap";
import { HashMap } from "../../utils/HashMap";
import { List } from "../../utils/List";
import AppModal from "../Generic/AppModal/AppModal";
import LoadingScaffold from "../LoadingScaffold";
import useModal from "../useModal";
import TimesheetScreen from "./TimesheetScreen";
import { SelectedDutyTasks, ValidTimesheetForm } from "./useTimesheetForm";

export interface Timesheet {
  clockInTime: LocalTime | null;
  clockOutTime: LocalTime | null;
  selectedDutyTasks: SelectedDutyTasks;
  requiredDutyTasks: List<RequiredDutySheetTask>;
  patientSignature: string | null;
  caregiverSignature: string | null;
  patientNotSignReason: string | null;
}

interface Props {
  appState: AppState;
  api: API;
  visitInstanceId: VisitInstanceId;
  onRequestNavigate: (page: Page) => void;
  onClickGoBack: () => void;
}

type State =
  | { type: "Loading" }
  | {
      type: "Ready";
      timesheet: Timesheet;
      visitInstance: VisitInstanceDetails;
      isCaregiverOfMedFlyt: boolean;
    };

const TimesheetActivity = (props: Props) => {
  const [timesheet, setTimesheet] = React.useState<Timesheet | Loading>(loading());

  const { scheduledVisits, appTasksState, appCaregiverState } = useMixin(props.appState);
  const modal = useModal();

  const handleClickVisit = (visitInstanceId: VisitInstanceId) => {
    props.onRequestNavigate({
      type: "VisitInstance",
      visitInstanceId: visitInstanceId,
    });
  };

  const handleSubmit = (form: ValidTimesheetForm) => {
    modal.setModalAsLoading();

    props.api
      .createTimeSheetByVisitInstanceId(props.visitInstanceId, {
        clockInTime: form.clockInTime,
        clockOutTime: form.clockOutTime,
        selectedDutyTasks: [...form.selectedDutyTasks.entries()].map(([id, { notes }]) => ({
          planOfCareItemId: id,
          notes,
        })),
        patientSignature: form.patientSignature,
        patientNotSignReason: form.patientNotSignReason,
        caregiverSignature: form.caregiverSignature,
      })
      .then((response) => {
        modal.setModalAsSuccess({
          title: translate(STRINGS.TIMESHEET_SUBMITTED),
          message: translate(STRINGS.YOUR_TIMESHEET_HAS_BEEN_SUBMITTED_SUCCESSFULLY),
          closeText: translate(STRINGS.GO_BACK),
          onRequestClose: () => {
            modal.setModalAsClosed();
            props.onClickGoBack();
          },
        });

        props.appState.setVisitInstanceOrTaskVisitInstanceClockInAndOutTime({
          visitInstanceId: props.visitInstanceId,
          clockInTime: fmap(response.clockInTime, (clockInTime) =>
            LocalDateTime.ofInstant(clockInTime)
          ),
          clockOutTime: fmap(response.clockOutTime, (clockOutTime) =>
            LocalDateTime.ofInstant(clockOutTime)
          ),
          timeSheetSubmittedAt: response.timeSheetSubmittedAt,
        });
      })
      .catch((error) => {
        const message = getApiErrorMessage({
          error,
          fallback: translate(STRINGS.AN_ERROR_OCCURED_WHILE_SUBMITTING_YOUR_TIMESHEET),
        });

        modal.setModalAsError({
          title: translate(STRINGS.SOMETHING_WENT_WRONG),
          message: message,
        });
      });

    console.log(form);
  };

  const state = ((): State => {
    if (
      isLoading(scheduledVisits) ||
      isLoading(appTasksState) ||
      isLoading(timesheet) ||
      isLoading(appCaregiverState)
    ) {
      return { type: "Loading" };
    }

    const visitInstanceFromVisit = scheduledVisits.scheduledVisitDetails.find(
      (x) => x.id === props.visitInstanceId
    );

    const scheduledTask = appTasksState.tasks.find(
      (t) => t.type === "SCHEDULED" && t.visitInstance?.id === props.visitInstanceId
    );

    const isCaregiverOfMedFlyt = appCaregiverState.caregiver.isCaregiverOfMedFlyt;

    const visitInstance = ((): VisitInstanceDetails | null => {
      if (visitInstanceFromVisit !== undefined) {
        return visitInstanceFromVisit;
      }

      if (scheduledTask !== undefined && scheduledTask.type === "SCHEDULED") {
        return scheduledTask.visitInstance;
      }

      return null;
    })();

    if (visitInstance === null) {
      throw new Error(`Visit instance ${props.visitInstanceId} not found`);
    }

    return {
      type: "Ready",
      visitInstance: visitInstance,
      timesheet: {
        ...timesheet,
        clockInTime: (
          timesheet.clockInTime ?? visitInstance.startTimeLocal.toLocalTime()
        ).truncatedTo(ChronoUnit.MINUTES),
        clockOutTime: (
          timesheet.clockOutTime ?? visitInstance.endTimeLocal.toLocalTime()
        ).truncatedTo(ChronoUnit.MINUTES),
      },
      isCaregiverOfMedFlyt: isCaregiverOfMedFlyt,
    };
  })();

  React.useEffect(() => {
    props.api.getTimeSheetByVisitInstanceId(props.visitInstanceId).then((response) => {
      setTimesheet({
        caregiverSignature: response.caregiverSignature,
        clockInTime: response.clockInTime,
        clockOutTime: response.clockOutTime,
        patientSignature: response.patientSignature,
        patientNotSignReason: response.patientNotSignReason,
        requiredDutyTasks: List.fromArray(response.requiredDutyTasks).map((task) => ({
          ...task,
          isRequired: false,
        })),
        selectedDutyTasks: HashMap.fromArray(
          response.selectedDuties.map((x) => [x.planOfCareItemId, { notes: x.notes }])
        ),
      });
    });
  }, [props.api, props.visitInstanceId]);

  switch (state.type) {
    case "Loading":
      return (
        <LoadingScaffold
          title={translate(STRINGS.SUBMIT_DUTIES)}
          onClickGoBack={props.onClickGoBack}
        />
      );
    case "Ready":
      return (
        <>
          <TimesheetScreen
            visitInstanceId={props.visitInstanceId}
            patientName={state.visitInstance.confidentialVisitDetails.patientName}
            visitStartTime={state.visitInstance.startTimeLocal}
            visitEndTime={state.visitInstance.endTimeLocal}
            timesheet={state.timesheet}
            isMedflytCaregiver={state.isCaregiverOfMedFlyt}
            onSubmit={handleSubmit}
            onClickVisit={handleClickVisit}
            onClickGoBack={props.onClickGoBack}
          />
          <AppModal {...modal.modalProps} />
        </>
      );
    default:
      assertNever(state);
  }
};

export default TimesheetActivity;

export function getApiErrorMessage({ error, fallback }: { error: unknown; fallback: string }) {
  try {
    if (typeof error === "object" && error !== null && "body" in error) {
      return JSON.parse((error as any).body).error ?? fallback;
    }

    return fallback;
  } catch {
    return fallback;
  }
}
